import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { useAdminAuth } from "@/context/Admin"
import Loader from "@/components/loader/loader"
import { toast } from "sonner"

export default function GeneralSettingsTab() {
  const { getAllSystemSettings, updateSystemSetting, getSiteInfo, updateSiteInfo } = useAdminAuth();
  
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [siteName, setSiteName] = useState("GlowSkin");
  const [siteDescription, setSiteDescription] = useState("Premium skincare products for radiant skin.");
  const [contactEmail, setContactEmail] = useState("support@glowskin.com");

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const [settings, siteInfo] = await Promise.all([
          getAllSystemSettings(),
          getSiteInfo(),
        ]);
        
        setMaintenanceMode(
          settings?.maintenance_mode_enabled?.value === true || 
          settings?.maintenance_mode_enabled?.value === 'true'
        );
        
        if (siteInfo) {
          setSiteName(siteInfo.siteName || "GlowSkin");
          setSiteDescription(siteInfo.siteDescription || "Premium skincare products for radiant skin.");
          setContactEmail(siteInfo.contactEmail || "support@glowskin.com");
        }
      } catch (error) {
        console.error("Error fetching general settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [getAllSystemSettings, getSiteInfo]);

  const handleMaintenanceModeToggle = async (enabled) => {
    setUpdating(true);
    try {
      await updateSystemSetting("maintenance_mode_enabled", enabled);
      setMaintenanceMode(enabled);
    } catch (error) {
      console.error("Error toggling maintenance mode:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveSiteInfo = async () => {
    setSaving(true);
    try {
      await updateSiteInfo({
        siteName,
        siteDescription,
        contactEmail,
      });
    } catch (error) {
      console.error("Error saving site information:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">Site Information</CardTitle>
          <CardDescription>Update basic information about your website.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="siteName">Site Name</Label>
            <Input 
              id="siteName" 
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              className="mt-1" 
            />
          </div>
          <div>
            <Label htmlFor="siteDescription">Site Description</Label>
            <Input
              id="siteDescription"
              value={siteDescription}
              onChange={(e) => setSiteDescription(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="contactEmail">Contact Email</Label>
            <Input 
              id="contactEmail" 
              type="email" 
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="mt-1" 
            />
          </div>
          <Button 
            className="bg-primary hover:bg-primary/90 text-white"
            onClick={handleSaveSiteInfo}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-primary">Maintenance Mode</CardTitle>
          <CardDescription>Temporarily disable public access to your site.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between">
          <div>
            <p className="font-medium text-primary">Enable Maintenance Mode</p>
            <p className="text-sm text-gray-600">
              Display a maintenance page to visitors while you perform updates.
            </p>
          </div>
          <Switch 
            checked={maintenanceMode} 
            onCheckedChange={handleMaintenanceModeToggle}
            disabled={updating}
          />
        </CardContent>
      </Card>
    </>
  )
}
